/**
 * Created by Ning Jiang on 7/4/2019.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Back, SlowMo, TimelineLite, TweenLite} from "gsap";
import {PixiesVsPiratesGameConfig} from "../../PixiesVsPiratesGameConfig";
import {BitmapTextStyleData} from "../../winpresentation/initial/PPWaysWonGFXText";

export class BattleMeterView extends BaseView {

    private readonly _barSize:[number, number] = [506, 50];
    private readonly _barEndSize:number = 80;
    private readonly _barPositions:[number, number][] = [[(GameConfig.instance.GAME_WIDTH - this._barSize[0]) / 2, 40], [(GameConfig.instance.GAME_WIDTH - this._barSize[0]) / 2 + this._barSize[0], 40]];
    private readonly _characterPositions:[number, number][][] = [
        [[160, 21 + 260], [1120, 21 + 260]], // landscape
        [[488, -329 + 260], [792, -329 + 260]] // portrait
    ];
    private readonly _spellPositions:[number, number][] = [[248 + 20, 200 - 80], [570 + 15, -162 - 70]];
    private readonly _gunPositions:[number, number][] = [[968 - 20, 305 - 70], [668 - 10, -74 - 50]];
    private readonly _characterScalePortrait:number = 1.1;
    private readonly _characterScaleLandscape:number = 1.15;
    private readonly _pickerOffsetYPortrait:number = -90;
    private _pickerOffsetYLandScape:number = 90 - 260 * this._characterScaleLandscape;

    private readonly _numberStyle:BitmapTextStyleData = {
        fontName : "NumbersPopFont",
        fontSize : 10,
        tint : 0xffffff
    };

    private _meterContainer:PIXI.Container;
    private _characterMask:PIXI.Graphics;
    private _landscapeMask:PIXI.Graphics;
    private _pixieContainer:PIXI.Container;
    private _pirateContainer:PIXI.Container;
    private _pixieIdle:TimelineSprite;
    private _pirateIdle:TimelineSprite;
    private _pixieDefeat:PIXI.Sprite;
    private _pirateDefeat:PIXI.Sprite;
    private _pixieBar:TimelineSprite;
    private _pirateBar:TimelineSprite;
    private _pixieMask:PIXI.Graphics;
    private _pirateMask:PIXI.Graphics;
    private _pixieHead:PIXI.Sprite;
    private _pirateHead:PIXI.Sprite;
    private _pixieNumber:PIXI.BitmapText;
    private _pirateNumber:PIXI.BitmapText;
    private _middleMarker:PIXI.Sprite;
    private _picker:PIXI.Sprite;
    private _versusLine:PIXI.Sprite;
    private _gunShot:TimelineSprite;
    private _spellHit:TimelineSprite;
    private _characterHitGlow:PIXI.Sprite;
    private _playerSelection:number;
    private _currentPosition:number;
    private _pickerPositionY:number;
    private _pickerAnimationOffset:number = 5;
    private _isShowing:boolean;

    private _layer:ScalableLayer;

    private get meterPosition():number {
        return this._currentPosition;
    }

    private set meterPosition(value:number) {
        if(value === this._currentPosition) {
            return;
        }
        const offset:number = this._barEndSize + (this._barSize[0] - this._barEndSize * 2) * value;
        const displayValue:number = offset / this._barSize[0];
        this._middleMarker.position.x = this._barPositions[0][0] + offset;
        this._pixieMask.scale.x = displayValue;
        this._pirateMask.scale.x = 1 - displayValue;
    }

    private get pickerPositionY():number {
        return this._pickerPositionY;
    }

    private set pickerPositionY(value:number) {
        this._picker.y = value;
    }

    constructor() {
        super();
    }

    protected initAnimations():void {
        this._layer = StageManager.getLayer(PixiesVsPiratesGameConfig.instance.LAYERS.METER.name);

        this._meterContainer = new PIXI.Container();
        this._meterContainer.visible = false;
        this._layer.addChild(this._meterContainer);

        this._characterMask = new PIXI.Graphics();
        this._characterMask.beginFill(0x00FF00, 1);
        this._characterMask.drawRect(GameConfig.instance.REEL_AREA_POS_X, this._characterPositions[1][0][1] - 260 * this._characterScalePortrait, GameConfig.instance.REEL_AREA_WIDTH, this._barPositions[0][1] - this._characterPositions[1][0][1] + 260 * this._characterScalePortrait);
        this._characterMask.visible = false;
        this._layer.addChild(this._characterMask);

        this._landscapeMask = new PIXI.Graphics();
        this._landscapeMask.visible = true;
        this._layer.addChild(this._landscapeMask);

        this._versusLine = new PIXI.Sprite(GameResources.getTextures("versusLine")[0]);
        this._versusLine.anchor.set(0.5);
        this._versusLine.scale.set(2);
        this._versusLine.blendMode = PIXI.BLEND_MODES.ADD;
        this._versusLine.position.set(GameConfig.instance.GAME_WIDTH / 2, -140);
        this._meterContainer.addChild(this._versusLine);

        this._pixieContainer = new PIXI.Container();
        this._meterContainer.addChild(this._pixieContainer);

        this._pixieIdle = new TimelineSprite(GameResources.getTextures("pixieBannerIdle"), 15);
        this._pixieIdle.anchor.set(0.5, 0.5);
        // this._pixieIdle.scale.set(1.2);
        this._pixieContainer.addChild(this._pixieIdle);

        this._pixieDefeat = new PIXI.Sprite(GameResources.getTextures("pixieDefeat")[0]);
        this._pixieDefeat.anchor.set(0.5, 0.5);
        this._pixieDefeat.alpha = 0;
        this._pixieContainer.addChild(this._pixieDefeat);

        this._pirateContainer = new PIXI.Container();
        this._meterContainer.addChild(this._pirateContainer);

        this._pirateIdle = new TimelineSprite(GameResources.getTextures("pirateBannerIdle"), 15);
        this._pirateIdle.anchor.set(0.55, 0.5);
        // this._pirateIdle.scale.set(1.2);
        this._pirateContainer.addChild(this._pirateIdle);

        this._pirateDefeat = new PIXI.Sprite(GameResources.getTextures("pirateDefeat")[0]);
        this._pirateDefeat.anchor.set(0.55, 0.54);
        this._pirateDefeat.alpha = 0;
        this._pirateContainer.addChild(this._pirateDefeat);

        this._pixieBar = new TimelineSprite(GameResources.getTextures("pixieBar"), 15);
        this._pixieBar.anchor.set(0, 0.5);
        this._pixieBar.position.set(this._barPositions[0][0], this._barPositions[0][1]);
        this._meterContainer.addChild(this._pixieBar);

        this._pirateBar = new TimelineSprite(GameResources.getTextures("pirateBar"), 15);
        this._pirateBar.anchor.set(1, 0.5);
        this._pirateBar.position.set(this._barPositions[1][0], this._barPositions[1][1]);
        this._meterContainer.addChild(this._pirateBar);

        this._pixieMask = new PIXI.Graphics();
        this._pixieMask.position.set(this._barPositions[0][0], this._barPositions[0][1]);
        this._pixieMask.beginFill(0xFF0000, 1);
        this._pixieMask.drawRect(0, -this._barSize[1] / 2, this._barSize[0], this._barSize[1]);
        this._pixieMask.endFill();
        this._meterContainer.addChild(this._pixieMask);
        this._pixieBar.mask = this._pixieMask;

        this._pirateMask = new PIXI.Graphics();
        this._pirateMask.position.set(this._barPositions[1][0], this._barPositions[1][1]);
        this._pirateMask.beginFill(0xFF0000, 1);
        this._pirateMask.drawRect(-this._barSize[0], -this._barSize[1] / 2, this._barSize[0], this._barSize[1]);
        this._pirateMask.endFill();
        this._meterContainer.addChild(this._pirateMask);
        this._pirateBar.mask = this._pirateMask;

        this._middleMarker = new PIXI.Sprite(GameResources.getTextures("barMarker")[0]);
        this._middleMarker.anchor.set(0.5);
        this._middleMarker.position.set(this._barPositions[1][0] / 2, this._barPositions[1][1]);
        this._middleMarker.blendMode = PIXI.BLEND_MODES.ADD;
        this._meterContainer.addChild(this._middleMarker);

        this._pixieHead = new PIXI.Sprite(GameResources.getTextures("pixieHead")[0]);
        this._pixieHead.anchor.set(0.5);
        this._pixieHead.position.set(this._barPositions[0][0], this._barPositions[0][1]);
        this._meterContainer.addChild(this._pixieHead);

        this._pirateHead = new PIXI.Sprite(GameResources.getTextures("pirateHead")[0]);
        this._pirateHead.anchor.set(0.5);
        this._pirateHead.position.set(this._barPositions[1][0], this._barPositions[1][1]);
        this._meterContainer.addChild(this._pirateHead);

        this._pixieNumber = new PIXI.BitmapText("0", this._numberStyle);
        (<PIXI.Point>this._pixieNumber.anchor).set(0.5, 0.5);
        this._pixieNumber.position.set(this._barPositions[0][0] + this._pixieHead.width / 2 + 5, this._barPositions[0][1] + 64);
        this._meterContainer.addChild(this._pixieNumber);

        this._pirateNumber = new PIXI.BitmapText("0", this._numberStyle);
        (<PIXI.Point>this._pirateNumber.anchor).set(0.5, 0.5);
        this._pirateNumber.position.set(this._barPositions[1][0] - this._pirateHead.width / 2 + 5, this._barPositions[1][1] + 64);
        this._meterContainer.addChild(this._pirateNumber);

        this._picker = new PIXI.Sprite(GameResources.getTextures("picker")[0]);
        this._picker.blendMode = PIXI.BLEND_MODES.ADD;
        this._picker.anchor.set(0.5);
        this._meterContainer.addChild(this._picker);

        this._characterHitGlow = new PIXI.Sprite(GameResources.getTextures("characterHitGlow")[0]);
        this._characterHitGlow.blendMode = PIXI.BLEND_MODES.ADD;
        this._characterHitGlow.anchor.set(0.5);
        this._characterHitGlow.alpha = 0;
        this._meterContainer.addChild(this._characterHitGlow);

        this._gunShot = new TimelineSprite(GameResources.getTextures("gunShot"));
        this._gunShot.blendMode = PIXI.BLEND_MODES.ADD;
        this._gunShot.anchor.set(0.5);
        this._gunShot.visible = false;
        this._meterContainer.addChild(this._gunShot);
        // this._gunShot.playLoop();

        this._spellHit = new TimelineSprite(GameResources.getTextures("spellHit"));
        this._spellHit.blendMode = PIXI.BLEND_MODES.ADD;
        this._spellHit.anchor.set(0.5);
        this._spellHit.visible = false;
        this._meterContainer.addChild(this._spellHit);
        // this._spellHit.playLoop();
    }

    public hide():void {
        this._isShowing = false;
        this._pixieBar.stopLoop();
        this._pirateBar.stopLoop();
        this._pixieIdle.stopLoop();
        this._pirateIdle.stopLoop();
        this._meterContainer.visible = false;
    }

    public init():void {
        this.animCharactersReset();
        this._isShowing = true;
        this.onResize(this._resizeData);
        this._pixieBar.playLoop();
        this._pirateBar.playLoop();
        this._pixieIdle.playLoop();
        this._pirateIdle.playLoop();
        this.setMeterPosition(0, 0);
        this.playMeterMarker();
        this._pickerOffsetYLandScape = 90 - 260 * this._characterScaleLandscape;
        this.playPicker();

        this._meterContainer.visible = true;
    }

    public setPlaySelection(value:number):void {
        this._playerSelection = value;
        this.resizePicker();
    }

    public setMeterPosition(a:number, b:number):void {
        if(!this._isShowing) {
            return;
        }
        this._pixieNumber.text = a.toString();
        this._pirateNumber.text = b.toString();
        const per:number = (a + b) === 0 ? 0.5 : MathHelper.roundToDecimals(a / (a + b), 2);
        this._currentPosition = per;
        this.meterPosition = per;
    }

    private playMeterMarker():void {
        if(!this._isShowing) {
            return;
        }
        TweenLite.to(this, this.getRandomeShakeTime(), {
            meterPosition : this._currentPosition + this.getRandomShakeOffset(),
            onComplete : () => this.playMeterMarker()
        });
    }

    private getRandomeShakeTime():number {
        return MathHelper.roundToDecimals(Math.random() * 0.15, 4);
    }

    private getRandomShakeOffset():number {
        return MathHelper.roundToDecimals(Math.random() * 0.03 - 0.015, 3);
    }

    private playPicker():void {
        if(!this._isShowing) {
            return;
        }
        TweenLite.to(this, 1,
            {
                pickerPositionY : this._pickerPositionY + this._pickerAnimationOffset,
                ease : SlowMo.ease.config(0.1, 0.1, true),
                onComplete : () => this.playPicker()
            });
    }

    public playPixieCollected():void {
        TweenLite.fromTo(this._pixieHead.scale, 0.3, {x : 1.15, y : 1.15}, {
            x : 1,
            y : 1,
            ease : Back.easeIn.config(1.7)
        });
    }

    public playPirateCollected():void {
        TweenLite.fromTo(this._pirateHead.scale, 0.3, {x : 1.15, y : 1.15}, {
            x : 1,
            y : 1,
            ease : Back.easeIn.config(1.7)
        });
    }

    public playDefeat(winnerIndex:number):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            TweenLite.to(this._pixieIdle, 0.1, {alpha : 1}),
            TweenLite.to(this._pixieDefeat, 0.1, {alpha : 0}),
            TweenLite.to(this._pirateIdle, 0.1, {alpha : 1}),
            TweenLite.to(this._pirateDefeat, 0.1, {alpha : 0})
        ]);
        if(winnerIndex === 0) {
            return;
        }

        const isPixieWin:boolean = winnerIndex === 1;
        const defeatPosition:[number, number] = this._characterPositions[StageManager.orientation === Orientation.PORTRAIT ? 1 : 0][isPixieWin ? 1 : 0];
        this._characterHitGlow.position.set(defeatPosition[0], defeatPosition[1]);

        timeline.add((isPixieWin ? this._spellHit : this._gunShot).getAnimationAutoShowHide());
        timeline.add([

            TweenLite.to((isPixieWin ? this._pixieIdle : this._pirateIdle).scale, 0.06, {x : 1.2, y : 1.2}) // scale up win idle over 2 frames
        ], 0.06);  // frame 2
        timeline.add([
            () => {
                TweenLite.fromTo(this._characterHitGlow, 0.2, {alpha : 1}, {alpha : 0});
            },
            TweenLite.to((isPixieWin ? this._pirateIdle : this._pixieIdle), 0.13, {alpha : 0}),// fade out lose idle over 4 frames
            TweenLite.to((isPixieWin ? this._pirateDefeat : this._pixieDefeat), 0.13, {alpha : 1}), // fade in lose defeat over 4 frames
            TweenLite.to((isPixieWin ? this._pirateDefeat : this._pixieDefeat).scale, 0.17, {x : 0.96, y : 0.96})// scale down lose defeat over 5 frames
        ], 0.25); // frame 4
        timeline.add([
            TweenLite.to(this._pixieIdle.scale, 0.1, {x : 1, y : 1}),
            TweenLite.to(this._pixieDefeat.scale, 0.1, {x : 1, y : 1}),
            TweenLite.to(this._pirateIdle.scale, 0.1, {x : 1, y : 1}),
            TweenLite.to(this._pirateDefeat.scale, 0.1, {x : 1, y : 1}),
        ], 0.4); // frame 12 reset all
    }

    public setCharsAnimStateOnRestore(pixiCount:number, pirateCount:number):void {
        if(pixiCount > pirateCount) {
            this._pixieIdle.alpha = 1;
            this._pixieDefeat.alpha = 0;
            this._pirateIdle.alpha = 0;
            this._pirateDefeat.alpha = 1;
        } else if(pirateCount > pixiCount) {
            this._pixieIdle.alpha = 0;
            this._pixieDefeat.alpha = 1;
            this._pirateIdle.alpha = 1;
            this._pirateDefeat.alpha = 0;
        }
    }

    public animateFinalBattleStatus(pixiCount:number, pirateCount:number, playerSelection:number):TimelineLite {
        const isPixieWin:boolean = pixiCount > pirateCount;
        const isEqual:boolean = pixiCount === pirateCount;
        const timeline:TimelineLite = new TimelineLite();
        if(!isEqual) {
            timeline.add([
                () => {
                    const delta:number = ((isPixieWin && playerSelection === 1) || (!isPixieWin && playerSelection === 2)) ? 30 : 0;
                    this.resizePicker(delta);
                    this._pickerOffsetYLandScape -= delta;
                },
                TweenLite.to((isPixieWin ? this._pixieIdle : this._pirateIdle).scale, 0.17, {x : 1.2, y : 1.2}),
                TweenLite.to((isPixieWin ? this._pirateDefeat : this._pixieDefeat).scale, 0.17, {x : 0.9, y : 0.9})
            ], 0.2);
        }
        timeline.set({}, {}, 1.5);
        return timeline;
    }

    private animCharactersReset():void {
        this._pixieIdle.alpha = 1;
        this._pixieDefeat.alpha = 0;
        this._pirateIdle.alpha = 1;
        this._pirateDefeat.alpha = 0;
        this._pirateIdle.scale.set(1);
        this._pirateDefeat.scale.set(1);
        this._pixieIdle.scale.set(1);
        this._pixieDefeat.scale.set(1);
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this.resizePicker();
        this.resizeCharacters();
        this._isResizeDirty = false;
    }

    private resizePicker(delta:number = 0):void {
        if(!this._isShowing) {
            return;
        }

        if(this._resizeData.orientation === Orientation.PORTRAIT) {
            this._picker.x = this._barPositions[this._playerSelection - 1][0];
            this._pickerPositionY = this._barPositions[this._playerSelection - 1][1] + this._pickerOffsetYPortrait;
        } else {
            this._picker.x = this._characterPositions[0][this._playerSelection - 1][0];
            this._pickerPositionY = this._characterPositions[0][this._playerSelection - 1][1] + this._pickerOffsetYLandScape - delta;
        }
        this.playPicker();
    }

    private resizeCharacters():void {
        if(!this._isShowing) {
            return;
        }

        if(this._resizeData.orientation === Orientation.PORTRAIT) {
            this._landscapeMask.visible = false;
            this._characterMask.visible = true;

            this._pixieContainer.scale.set(this._characterScalePortrait);
            this._pixieContainer.position.set(this._characterPositions[1][0][0], this._characterPositions[1][0][1]);
            this._pixieContainer.mask = this._characterMask;
            this._spellHit.position.set(this._spellPositions[1][0], this._spellPositions[1][1]);
            this._spellHit.scale.set(2);

            this._pirateContainer.scale.set(this._characterScalePortrait);
            this._pirateContainer.position.set(this._characterPositions[1][1][0], this._characterPositions[1][1][1]);
            this._pirateContainer.mask = this._characterMask;
            this._gunShot.position.set(this._gunPositions[1][0], this._gunPositions[1][1]);
            this._gunShot.scale.set(2);

            this._characterHitGlow.scale.set(5);
            this._versusLine.visible = true;
        } else {
            const screenWidth:number = Math.ceil(this._resizeData.width / this._layer.scale.x);
            const screenHeight:number = Math.ceil(this._resizeData.height / this._layer.scale.y);
            const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
            const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

            this._landscapeMask.visible = true;
            this._characterMask.visible = false;
            this._landscapeMask.clear();
            this._landscapeMask.beginFill(0x00FF00, 1);
            this._landscapeMask.drawRect(leftOffset, topOffset, screenWidth, screenHeight);

            this._pixieContainer.scale.set(this._characterScaleLandscape);
            this._pixieContainer.position.set(this._characterPositions[0][0][0], this._characterPositions[0][0][1]);
            this._pixieContainer.mask = this._landscapeMask;
            this._spellHit.position.set(this._spellPositions[0][0], this._spellPositions[0][1]);
            this._spellHit.scale.set(2.2);

            this._pirateContainer.scale.set(this._characterScaleLandscape);
            this._pirateContainer.position.set(this._characterPositions[0][1][0], this._characterPositions[0][1][1]);
            this._pirateContainer.mask = this._landscapeMask;
            this._gunShot.position.set(this._gunPositions[0][0], this._gunPositions[0][1]);
            this._gunShot.scale.set(2.2);

            this._characterHitGlow.scale.set(6);
            this._versusLine.visible = false;
        }
    }
}